let Shaders = {};

Shaders["pointcloud.vs"] = `#version 300 es
// pointcloud.vs
precision highp float;

#define max_clip_polygons 8
#define PI 3.141592653589793

in vec3 position;
in vec3 color;
in float intensity;
in float classification;
in float returnNumber;
in float numberOfReturns;
in float pointSourceID;
in vec4 indices;
in float spacing;
in float gpsTime;
in vec3 normal;
in float aExtra;

uniform mat4 modelMatrix;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat4 viewMatrix;
uniform mat4 uViewInv;

uniform float uScreenWidth;
uniform float uScreenHeight;
uniform float fov;
uniform float near;
uniform float far;

uniform bool uDebug;

uniform bool uUseOrthographicCamera;
uniform float uOrthoWidth;
uniform float uOrthoHeight;

#define CLIPTASK_NONE 0
#define CLIPTASK_HIGHLIGHT 1
#define CLIPTASK_SHOW_INSIDE 2
#define CLIPTASK_SHOW_OUTSIDE 3

#define CLIPMETHOD_INSIDE_ANY 0
#define CLIPMETHOD_INSIDE_ALL 1

uniform int clipTask;
uniform int clipMethod;
#if defined(num_clipboxes) && num_clipboxes > 0
	uniform mat4 clipBoxes[num_clipboxes];
	uniform vec3 clipBoxColors[num_clipboxes];
#endif

#if defined(num_clipspheres) && num_clipspheres > 0
	uniform mat4 uClipSpheres[num_clipspheres];
#endif

#if defined(num_clippolygons) && num_clippolygons > 0
	uniform int uClipPolygonVCount[num_clippolygons];
	uniform vec3 uClipPolygonVertices[num_clippolygons * 8];
	uniform mat4 uClipPolygonWVP[num_clippolygons];
#endif


uniform float size;
uniform float minSize;
uniform float maxSize;

uniform float uPCIndex;
uniform float uOctreeSpacing;
uniform float uNodeSpacing;
uniform float uOctreeSize;
uniform vec3 uBBSize;
uniform float uLevel;
uniform float uVNStart;
uniform bool uIsLeafNode;

uniform vec3 uColor;
uniform float uOpacity;

#define SURFACE_MAX 8
#define SURFACE_TEX_W 2048
// Occupied shell voxels with |n·plane| below this are perpendicular faces
// (≳ 70°). Must match SHELL_ORTHO_MAX_ABS_DOT in noach-be constants.ts.
const float SURFACE_SHELL_ORTHO_MAX_ABS_DOT = 0.35;
// Snap onto grown neighbours this far (Chebyshev) if the IJK step is in-plane.
const int SURFACE_SNAP_CHEB = 2;
const int SURFACE_HASH_MAX_PROBE = 32;
uniform int uSurfaceCount;
uniform vec3 uSurfacePlaneOrigin[SURFACE_MAX];
uniform vec3 uSurfacePlaneNormal[SURFACE_MAX];
uniform float uSurfaceMaxDistance[SURFACE_MAX];
uniform float uSurfaceCosAngle[SURFACE_MAX];
uniform vec3 uSurfaceColor[SURFACE_MAX];
uniform vec3 uSurfaceIjkOrigin[SURFACE_MAX];
uniform vec3 uSurfaceAabbMin[SURFACE_MAX];
uniform vec3 uSurfaceAabbMax[SURFACE_MAX];
uniform float uSurfaceCellSize[SURFACE_MAX];
uniform int uSurfaceOccCount[SURFACE_MAX];
uniform int uSurfaceBlockCount[SURFACE_MAX];
uniform int uSurfaceHashSize[SURFACE_MAX];
uniform int uSurfaceShellHashSize[SURFACE_MAX];
uniform int uSurfaceFrontierHashSize[SURFACE_MAX];
uniform int uSurfaceSnapHashSize[SURFACE_MAX];
// Height profile: 0 off, 1 continuous gradient, N (2 / 4 / 8) = N discrete
// colour bands. Overloaded to stay inside the vertex-uniform budget; test != 0.
uniform int uSurfaceRainbow[SURFACE_MAX];
uniform int uSurfaceGrowth[SURFACE_MAX];
uniform int uSurfaceFlipped[SURFACE_MAX];
// Height-profile zero level (flip-aware, incl. the user's offset) and gradient
// half-width. Display only — uSurfaceMaxDistance still decides membership.
uniform float uSurfaceProfileLevel[SURFACE_MAX];
uniform float uSurfaceProfileRangeBelow[SURFACE_MAX];
uniform float uSurfaceProfileRangeAbove[SURFACE_MAX];
// Colouring normal only — equals the plane normal in fitted mode, the snapped
// plumb/level normal in ideal mode. Membership stays on uSurfacePlaneNormal.
uniform vec3 uSurfaceProfileNormal[SURFACE_MAX];
uniform float uSurfaceTexHeight[SURFACE_MAX];
uniform int uSurfaceSnap;
// Node → pointcloud-local, computed in JS float64. Do not reconstruct this
// from world CRS in the shader: Bojszowy-scale translations (~1e7) lose more
// than a 4 cm occupancy cell in float32, so membership never hits.
uniform mat4 uNodeInPc;
// geomOffset - metaOffset (small; often zero). Also computed in JS.
uniform vec3 uOffsetAdjust;
uniform sampler2D uSurfaceOcc0;
uniform sampler2D uSurfaceOcc1;
uniform sampler2D uSurfaceOcc2;
uniform sampler2D uSurfaceOcc3;
uniform sampler2D uSurfaceOcc4;
uniform sampler2D uSurfaceOcc5;
uniform sampler2D uSurfaceOcc6;
uniform sampler2D uSurfaceOcc7;

uniform vec2 elevationRange;
uniform vec2 intensityRange;

uniform vec2 uFilterReturnNumberRange;
uniform vec2 uFilterNumberOfReturnsRange;
uniform vec2 uFilterPointSourceIDClipRange;
uniform vec2 uFilterGPSTimeClipRange;
uniform int uElevationClipEnabled;
uniform float uGpsScale;
uniform float uGpsOffset;

uniform vec2 uNormalizedGpsBufferRange;

uniform vec3 uIntensity_gbc;
uniform vec3 uRGB_gbc;
uniform vec3 uExtra_gbc;

uniform float uTransition;
uniform float wRGB;
uniform float wIntensity;
uniform float wElevation;
uniform float wClassification;
uniform float wReturnNumber;
uniform float wSourceID;

uniform vec2 uExtraNormalizedRange;
uniform vec2 uExtraRange;
uniform float uExtraScale;
uniform float uExtraOffset;

uniform vec3 uShadowColor;

uniform sampler2D visibleNodes;
uniform sampler2D gradient;
uniform sampler2D classificationLUT;

#if defined(color_type_matcap)
uniform sampler2D matcapTextureUniform;
#endif
uniform bool backfaceCulling;

#if defined(num_shadowmaps) && num_shadowmaps > 0
uniform sampler2D uShadowMap[num_shadowmaps];
uniform mat4 uShadowWorldView[num_shadowmaps];
uniform mat4 uShadowProj[num_shadowmaps];
#endif

out vec3	vColor;
out float	vLogDepth;
out vec3	vViewPosition;
out float 	vRadius;
out float 	vPointSize;

//
//    ###    ########     ###    ########  ######## #### ##     ## ########     ######  #### ######## ########  ######
//   ## ##   ##     ##   ## ##   ##     ##    ##     ##  ##     ## ##          ##    ##  ##       ##  ##       ##    ##
//  ##   ##  ##     ##  ##   ##  ##     ##    ##     ##  ##     ## ##          ##        ##      ##   ##       ##
// ##     ## ##     ## ##     ## ########     ##     ##  ##     ## ######       ######   ##     ##    ######    ######
// ######### ##     ## ######### ##           ##     ##   ##   ##  ##                ##  ##    ##     ##             ##
// ##     ## ##     ## ##     ## ##           ##     ##    ## ##   ##          ##    ##  ##   ##      ##       ##    ##
// ##     ## ########  ##     ## ##           ##    ####    ###    ########     ######  #### ######## ########  ######
//


// ---------------------
// OCTREE
// ---------------------

#if (defined(adaptive_point_size) || defined(color_type_level_of_detail)) && defined(tree_type_octree)
/**
 * number of 1-bits up to inclusive index position
 * number is treated as if it were an integer in the range 0-255
 *
 */
int numberOfOnes(int number, int index){
	int numOnes = 0;
	int tmp = 128;
	for(int i = 7; i >= 0; i--){

		if(number >= tmp){
			number = number - tmp;

			if(i <= index){
				numOnes++;
			}
		}

		tmp = tmp / 2;
	}

	return numOnes;
}


/**
 * checks whether the bit at index is 1
 * number is treated as if it were an integer in the range 0-255
 *
 */
bool isBitSet(int number, int index){

	// weird multi else if due to lack of proper array, int and bitwise support in WebGL 1.0
	int powi = 1;
	if(index == 0){
		powi = 1;
	}else if(index == 1){
		powi = 2;
	}else if(index == 2){
		powi = 4;
	}else if(index == 3){
		powi = 8;
	}else if(index == 4){
		powi = 16;
	}else if(index == 5){
		powi = 32;
	}else if(index == 6){
		powi = 64;
	}else if(index == 7){
		powi = 128;
	}else{
		return false;
	}

	int ndp = number / powi;

	return mod(float(ndp), 2.0) != 0.0;
}


/**
 * find the LOD at the point position
 */
float getLOD(){

	vec3 offset = vec3(0.0, 0.0, 0.0);
	int iOffset = int(uVNStart);
	float depth = uLevel;
	for(float i = 0.0; i <= 30.0; i++){
		float nodeSizeAtLevel = uOctreeSize / pow(2.0, i + uLevel + 0.0);

		vec3 index3d = (position-offset) / nodeSizeAtLevel;
		index3d = floor(index3d + 0.5);
		int index = int(round(4.0 * index3d.x + 2.0 * index3d.y + index3d.z));

		vec4 value = texture(visibleNodes, vec2(float(iOffset) / 2048.0, 0.0));
		int mask = int(round(value.r * 255.0));

		if(isBitSet(mask, index)){
			// there are more visible child nodes at this position
			int advanceG = int(round(value.g * 255.0)) * 256;
			int advanceB = int(round(value.b * 255.0));
			int advanceChild = numberOfOnes(mask, index - 1);
			int advance = advanceG + advanceB + advanceChild;

			iOffset = iOffset + advance;

			depth++;
		}else{
			// no more visible child nodes at this position
			//return value.a * 255.0;

			float lodOffset = (255.0 * value.a) / 10.0 - 10.0;

			return depth  + lodOffset;
		}

		offset = offset + (vec3(1.0, 1.0, 1.0) * nodeSizeAtLevel * 0.5) * index3d;
	}

	return depth;
}

float getSpacing(){
	vec3 offset = vec3(0.0, 0.0, 0.0);
	int iOffset = int(uVNStart);
	float depth = uLevel;
	float spacing = uNodeSpacing;
	for(float i = 0.0; i <= 30.0; i++){
		float nodeSizeAtLevel = uOctreeSize / pow(2.0, i + uLevel + 0.0);

		vec3 index3d = (position-offset) / nodeSizeAtLevel;
		index3d = floor(index3d + 0.5);
		int index = int(round(4.0 * index3d.x + 2.0 * index3d.y + index3d.z));

		vec4 value = texture(visibleNodes, vec2(float(iOffset) / 2048.0, 0.0));
		int mask = int(round(value.r * 255.0));
		float spacingFactor = value.a;

		if(i > 0.0){
			spacing = spacing / (255.0 * spacingFactor);
		}


		if(isBitSet(mask, index)){
			// there are more visible child nodes at this position
			int advanceG = int(round(value.g * 255.0)) * 256;
			int advanceB = int(round(value.b * 255.0));
			int advanceChild = numberOfOnes(mask, index - 1);
			int advance = advanceG + advanceB + advanceChild;

			iOffset = iOffset + advance;

			//spacing = spacing / (255.0 * spacingFactor);
			//spacing = spacing / 3.0;

			depth++;
		}else{
			// no more visible child nodes at this position
			return spacing;
		}

		offset = offset + (vec3(1.0, 1.0, 1.0) * nodeSizeAtLevel * 0.5) * index3d;
	}

	return spacing;
}

float getPointSizeAttenuation(){
	return pow(2.0, getLOD());
}


#endif


// ---------------------
// KD-TREE
// ---------------------

#if (defined(adaptive_point_size) || defined(color_type_level_of_detail)) && defined(tree_type_kdtree)

float getLOD(){
	vec3 offset = vec3(0.0, 0.0, 0.0);
	float iOffset = 0.0;
	float depth = 0.0;


	vec3 size = uBBSize;
	vec3 pos = position;

	for(float i = 0.0; i <= 1000.0; i++){

		vec4 value = texture(visibleNodes, vec2(iOffset / 2048.0, 0.0));

		int children = int(value.r * 255.0);
		float next = value.g * 255.0;
		int split = int(value.b * 255.0);

		if(next == 0.0){
		 	return depth;
		}

		vec3 splitv = vec3(0.0, 0.0, 0.0);
		if(split == 1){
			splitv.x = 1.0;
		}else if(split == 2){
		 	splitv.y = 1.0;
		}else if(split == 4){
		 	splitv.z = 1.0;
		}

		iOffset = iOffset + next;

		float factor = length(pos * splitv / size);
		if(factor < 0.5){
			// left
		if(children == 0 || children == 2){
				return depth;
			}
		}else{
			// right
			pos = pos - size * splitv * 0.5;
			if(children == 0 || children == 1){
				return depth;
			}
			if(children == 3){
				iOffset = iOffset + 1.0;
			}
		}
		size = size * ((1.0 - (splitv + 1.0) / 2.0) + 0.5);

		depth++;
	}


	return depth;
}

float getPointSizeAttenuation(){
	return 0.5 * pow(1.3, getLOD());
}

#endif



//
//    ###    ######## ######## ########  #### ########  ##     ## ######## ########  ######
//   ## ##      ##       ##    ##     ##  ##  ##     ## ##     ##    ##    ##       ##    ##
//  ##   ##     ##       ##    ##     ##  ##  ##     ## ##     ##    ##    ##       ##
// ##     ##    ##       ##    ########   ##  ########  ##     ##    ##    ######    ######
// #########    ##       ##    ##   ##    ##  ##     ## ##     ##    ##    ##             ##
// ##     ##    ##       ##    ##    ##   ##  ##     ## ##     ##    ##    ##       ##    ##
// ##     ##    ##       ##    ##     ## #### ########   #######     ##    ########  ######
//



// formula adapted from: http://www.dfstudios.co.uk/articles/programming/image-programming-algorithms/image-processing-algorithms-part-5-contrast-adjustment/
float getContrastFactor(float contrast){
	return (1.0158730158730156 * (contrast + 1.0)) / (1.0158730158730156 - contrast);
}

vec3 getRGB(){
	vec3 rgb = color;

	rgb = pow(rgb, vec3(uRGB_gbc.x));
	rgb = rgb + uRGB_gbc.y;
	rgb = (rgb - 0.5) * getContrastFactor(uRGB_gbc.z) + 0.5;
	rgb = clamp(rgb, 0.0, 1.0);

	return rgb;
}

float getIntensity(){
	float w = (intensity - intensityRange.x) / (intensityRange.y - intensityRange.x);
	w = pow(w, uIntensity_gbc.x);
	w = w + uIntensity_gbc.y;
	w = (w - 0.5) * getContrastFactor(uIntensity_gbc.z) + 0.5;
	w = clamp(w, 0.0, 1.0);

	return w;
}

vec3 getGpsTime(){

	float w = (gpsTime + uGpsOffset) * uGpsScale;


	vec3 c = texture(gradient, vec2(w, 1.0 - w)).rgb;


	// vec2 r = uNormalizedGpsBufferRange;
	// float w = gpsTime * (r.y - r.x) + r.x;
	// w = clamp(w, 0.0, 1.0);
	// vec3 c = texture(gradient, vec2(w,1.0-w)).rgb;

	return c;
}

vec3 getElevation(){
	vec4 world = modelMatrix * vec4( position, 1.0 );
	float w = (world.z - elevationRange.x) / (elevationRange.y - elevationRange.x);

	if (uElevationClipEnabled == 1 && (w < 0.0 || w > 1.0)) {
		return vec3(0.0, 0.0, 0.0);
	}

	vec3 cElevation = texture(gradient, vec2(w,1.0-w)).rgb;
	return cElevation;
}

vec4 getClassification(){
	vec2 uv = vec2(classification / 255.0, 0.5);
	vec4 classColor = texture(classificationLUT, uv);

	return classColor;
}

vec3 getReturns(){

	// 0b 00_000_111
	float rn = mod(returnNumber, 8.0);
	// 0b 00_111_000
	float nr = mod(returnNumber / 8.0, 8.0);

	if(nr <= 1.0){
		return vec3(1.0, 0.0, 0.0);
	}else{
		return vec3(0.0, 1.0, 0.0);
	}

	// return vec3(nr / 4.0, 0.0, 0.0);

	// if(nr == 1.0){
	// 	return vec3(1.0, 1.0, 0.0);
	// }else{
	// 	if(rn == 1.0){
	// 		return vec3(1.0, 0.0, 0.0);
	// 	}else if(rn == nr){
	// 		return vec3(0.0, 0.0, 1.0);
	// 	}else{
	// 		return vec3(0.0, 1.0, 0.0);
	// 	}
	// }

	// if(numberOfReturns == 1.0){
	// 	return vec3(1.0, 1.0, 0.0);
	// }else{
	// 	if(returnNumber == 1.0){
	// 		return vec3(1.0, 0.0, 0.0);
	// 	}else if(returnNumber == numberOfReturns){
	// 		return vec3(0.0, 0.0, 1.0);
	// 	}else{
	// 		return vec3(0.0, 1.0, 0.0);
	// 	}
	// }
}

vec3 getReturnNumber(){
	if(numberOfReturns == 1.0){
		return vec3(1.0, 1.0, 0.0);
	}else{
		if(returnNumber == 1.0){
			return vec3(1.0, 0.0, 0.0);
		}else if(returnNumber == numberOfReturns){
			return vec3(0.0, 0.0, 1.0);
		}else{
			return vec3(0.0, 1.0, 0.0);
		}
	}
}

vec3 getNumberOfReturns(){
	float value = numberOfReturns;

	float w = value / 6.0;

	vec3 color = texture(gradient, vec2(w, 1.0 - w)).rgb;

	return color;
}

vec3 getSourceID(){
	float w = mod(pointSourceID, 10.0) / 10.0;
	return texture(gradient, vec2(w,1.0 - w)).rgb;
}

vec3 getCompositeColor(){
	vec3 c;
	float w;

	c += wRGB * getRGB();
	w += wRGB;

	c += wIntensity * getIntensity() * vec3(1.0, 1.0, 1.0);
	w += wIntensity;

	c += wElevation * getElevation();
	w += wElevation;

	c += wReturnNumber * getReturnNumber();
	w += wReturnNumber;

	c += wSourceID * getSourceID();
	w += wSourceID;

	vec4 cl = wClassification * getClassification();
	c += cl.a * cl.rgb;
	w += wClassification * cl.a;

	c = c / w;

	if(w == 0.0){
		//c = color;
		gl_Position = vec4(100.0, 100.0, 100.0, 0.0);
	}

	return c;
}


vec3 getNormal(){
	//vec3 n_hsv = vec3( modelMatrix * vec4( normal, 0.0 )) * 0.5 + 0.5; // (n_world.xyz + vec3(1.,1.,1.)) / 2.;
	vec3 n_view = normalize( vec3(modelViewMatrix * vec4( normal, 0.0 )) );
	return n_view;
}
bool applyBackfaceCulling() {
	// Black not facing vertices / Backface culling
	vec3 e = normalize(vec3(modelViewMatrix * vec4( position, 1. )));
	vec3 n = getNormal(); // normalize( vec3(modelViewMatrix * vec4( normal, 0.0 )) );

	if((uUseOrthographicCamera && n.z <= 0.) || (!uUseOrthographicCamera && dot( n, e ) >= 0.)) {
		return true;
	} else {
		return false;
	}
}

#if defined(color_type_matcap)
// Matcap Material
vec3 getMatcap(){
	vec3 eye = normalize( vec3( modelViewMatrix * vec4( position, 1. ) ) );
	if(uUseOrthographicCamera) {
		eye = vec3(0., 0., -1.);
	}
	vec3 r_en = reflect( eye, getNormal() ); // or r_en = e - 2. * dot( n, e ) * n;
	float m = 2. * sqrt(pow( r_en.x, 2. ) + pow( r_en.y, 2. ) + pow( r_en.z + 1., 2. ));
	vec2 vN = r_en.xy / m + .5;
	return texture(matcapTextureUniform, vN).rgb;
}
#endif

vec3 getExtra(){

	float w = (aExtra + uExtraOffset) * uExtraScale;
	w = clamp(w, 0.0, 1.0);

	vec3 color = texture(gradient, vec2(w,1.0-w)).rgb;

	// vec2 r = uExtraNormalizedRange;

	// float w = aExtra * (r.y - r.x) + r.x;

	// w = (w - uExtraRange.x) / (uExtraRange.y - uExtraRange.x);

	// w = clamp(w, 0.0, 1.0);

	// vec3 color = texture(gradient, vec2(w,1.0-w)).rgb;

	return color;
}

vec3 getColor(){
	vec3 color;

	#ifdef color_type_rgba
		color = getRGB();
	#elif defined color_type_height || defined color_type_elevation
		color = getElevation();
	#elif defined color_type_rgb_height
		vec3 cHeight = getElevation();
		color = (1.0 - uTransition) * getRGB() + uTransition * cHeight;
	#elif defined color_type_depth
		float linearDepth = gl_Position.w;
		float expDepth = (gl_Position.z / gl_Position.w) * 0.5 + 0.5;
		color = vec3(linearDepth, expDepth, 0.0);
		//color = vec3(1.0, 0.5, 0.3);
	#elif defined color_type_intensity
		float w = getIntensity();
		color = vec3(w, w, w);
	#elif defined color_type_gps_time
		color = getGpsTime();
	#elif defined color_type_intensity_gradient
		float w = getIntensity();
		color = texture(gradient, vec2(w,1.0-w)).rgb;
	#elif defined color_type_color
		color = uColor;
	#elif defined color_type_level_of_detail
		float depth = getLOD();
		float w = depth / 10.0;
		color = texture(gradient, vec2(w,1.0-w)).rgb;
	#elif defined color_type_indices
		color = indices.rgb;
	#elif defined color_type_classification
		vec4 cl = getClassification();
		color = cl.rgb;
	#elif defined color_type_return_number
		color = getReturnNumber();
	#elif defined color_type_returns
		color = getReturns();
	#elif defined color_type_number_of_returns
		color = getNumberOfReturns();
	#elif defined color_type_source_id
		color = getSourceID();
	#elif defined color_type_point_source_id
		color = getSourceID();
	#elif defined color_type_normal
		color = (modelMatrix * vec4(normal, 0.0)).xyz;
	#elif defined color_type_phong
		color = color;
	#elif defined color_type_composite
		color = getCompositeColor();
	#elif defined color_type_matcap
		color = getMatcap();
	#else
		color = getExtra();
	#endif

	if (backfaceCulling && applyBackfaceCulling()) {
		color = vec3(0.);
	}

	return color;
}

int surfaceU8(float c) {
	return int(clamp(c, 0.0, 1.0) * 255.0 + 0.5);
}

int surfaceU16(float lo, float hi) {
	return surfaceU8(lo) + surfaceU8(hi) * 256;
}

vec4 surfaceTexel(int surf, ivec2 p) {
	if (surf == 0) return texelFetch(uSurfaceOcc0, p, 0);
	if (surf == 1) return texelFetch(uSurfaceOcc1, p, 0);
	if (surf == 2) return texelFetch(uSurfaceOcc2, p, 0);
	if (surf == 3) return texelFetch(uSurfaceOcc3, p, 0);
	if (surf == 4) return texelFetch(uSurfaceOcc4, p, 0);
	if (surf == 5) return texelFetch(uSurfaceOcc5, p, 0);
	if (surf == 6) return texelFetch(uSurfaceOcc6, p, 0);
	return texelFetch(uSurfaceOcc7, p, 0);
}

bool surfaceReadVoxelCode(int surf, int index, out ivec3 ijk, out int code) {
	int p = index * 2;
	int x = p % SURFACE_TEX_W;
	int y = p / SURFACE_TEX_W;
	vec4 a = surfaceTexel(surf, ivec2(x, y));
	vec4 b = surfaceTexel(surf, ivec2(x + 1, y));
	ijk = ivec3(surfaceU16(a.r, a.g), surfaceU16(a.b, a.a), surfaceU16(b.r, b.g));
	code = surfaceU16(b.b, b.a);
	return true;
}

bool surfaceReadVoxel(int surf, int index, out ivec3 ijk, out vec3 nrm) {
	int code;
	surfaceReadVoxelCode(surf, index, ijk, code);
	float ox = (float(code & 255) / 255.0) * 2.0 - 1.0;
	float oy = (float((code >> 8) & 255) / 255.0) * 2.0 - 1.0;
	float oz = 1.0 - abs(ox) - abs(oy);
	float rx = ox;
	float ry = oy;
	if (oz < 0.0) {
		rx = (1.0 - abs(oy)) * (ox >= 0.0 ? 1.0 : -1.0);
		ry = (1.0 - abs(ox)) * (oy >= 0.0 ? 1.0 : -1.0);
	}
	float len = length(vec3(rx, ry, oz));
	nrm = len < 1e-8 ? vec3(0.0, 0.0, 1.0) : vec3(rx, ry, oz) / len;
	return true;
}

int surfaceKeyCmp(ivec3 a, ivec3 b) {
	if (a.x != b.x) return a.x < b.x ? -1 : 1;
	if (a.y != b.y) return a.y < b.y ? -1 : 1;
	if (a.z != b.z) return a.z < b.z ? -1 : 1;
	return 0;
}

uint surfaceHashIjk(ivec3 g) {
	uint x = (uint(g.x) & 65535u) * 73856093u;
	uint y = (uint(g.y) & 65535u) * 19349663u;
	uint z = (uint(g.z) & 65535u) * 83492791u;
	return x ^ y ^ z;
}

bool surfaceHashHas(int surf, ivec3 want, int base, int hsize, out vec3 nrm) {
	if (hsize <= 0) return false;
	uint mask = uint(hsize - 1);
	uint h = surfaceHashIjk(want);
	uint stride = (h >> 16) | 1u;
	for (int probe = 0; probe < SURFACE_HASH_MAX_PROBE; probe++) {
		int slot = base + int((h + uint(probe) * stride) & mask);
		ivec3 ijk;
		vec3 n;
		surfaceReadVoxel(surf, slot, ijk, n);
		if (ijk.x == 65535 && ijk.y == 65535 && ijk.z == 65535) return false;
		if (surfaceKeyCmp(ijk, want) == 0) {
			nrm = n;
			return true;
		}
	}
	return false;
}

bool surfaceHashReason(int surf, ivec3 want, int base, int hsize, out int reason) {
	if (hsize <= 0) return false;
	uint mask = uint(hsize - 1);
	uint h = surfaceHashIjk(want);
	uint stride = (h >> 16) | 1u;
	for (int probe = 0; probe < SURFACE_HASH_MAX_PROBE; probe++) {
		int slot = base + int((h + uint(probe) * stride) & mask);
		ivec3 ijk;
		int code;
		surfaceReadVoxelCode(surf, slot, ijk, code);
		if (ijk.x == 65535 && ijk.y == 65535 && ijk.z == 65535) return false;
		if (surfaceKeyCmp(ijk, want) == 0) {
			reason = code;
			return true;
		}
	}
	return false;
}

bool surfacePassesAngle(int surf, vec3 nrm, float residual, float cell) {
	float ndot = dot(nrm, uSurfacePlaneNormal[surf]);
	if (ndot >= uSurfaceCosAngle[surf]) return true;
	return ndot <= -uSurfaceCosAngle[surf] && abs(residual) <= 0.5 * cell;
}

// True when the occupancy step stays on the wall sheet (not along the normal).
bool surfaceStepInPlane(ivec3 d, vec3 planeN) {
	vec3 s = vec3(d);
	float len = length(s);
	if (len < 0.5) return false;
	return abs(dot(s / len, planeN)) <= 0.5;
}

bool surfaceMembership(int surf, vec3 offsetP, out float residual, out vec3 nrm, out ivec3 memberIjk) {
	residual = 0.0;
	memberIjk = ivec3(-1);
	float cell = uSurfaceCellSize[surf];
	if (cell <= 1e-8) return false;
	vec3 originIjk = uSurfaceIjkOrigin[surf];
	ivec3 gi = ivec3(floor(offsetP / cell)) - ivec3(originIjk);
	int pad = SURFACE_SNAP_CHEB;
	ivec3 bmin = ivec3(uSurfaceAabbMin[surf]) - ivec3(pad);
	ivec3 bmax = ivec3(uSurfaceAabbMax[surf]) + ivec3(pad);
	if (gi.x < bmin.x || gi.y < bmin.y || gi.z < bmin.z) return false;
	if (gi.x > bmax.x || gi.y > bmax.y || gi.z > bmax.z) return false;
	vec3 planeO = uSurfacePlaneOrigin[surf];
	vec3 planeN = uSurfacePlaneNormal[surf];
	residual = dot(offsetP - planeO, planeN);
	int nMemHash = uSurfaceHashSize[surf];
	int nBlkHash = uSurfaceShellHashSize[surf];
	vec3 foundN;
	// Exact IJK in the grown set. Grow already applied residual / angle /
	// on-plane-barrier rules; packed occupancy normals on sparse roof cells
	// are often garbage and must not undo membership.
	if (surfaceHashHas(surf, gi, 0, nMemHash, foundN)) {
		nrm = foundN;
		memberIjk = gi;
		return true;
	}
	if (abs(residual) > uSurfaceMaxDistance[surf] + 0.5 * cell) return false;
	// Occupied 1-cell shell. Perpendicular neighbours (floor, reveal) are
	// hard stops. Same-sheet cells with noisy PCA fall through to member
	// snap so interior plaster holes still highlight.
	if (surfaceHashHas(surf, gi, nMemHash, nBlkHash, foundN)) {
		if (surfacePassesAngle(surf, foundN, residual, cell)) {
			nrm = foundN;
			return true;
		}
		if (abs(dot(foundN, planeN)) < SURFACE_SHELL_ORTHO_MAX_ABS_DOT) {
			nrm = foundN;
			return false;
		}
	}
	// Empty / misoriented same-sheet cell: in-plane Chebyshev-2 halo baked
	// into the occupancy texture so highlight does not drop while the
	// camera moves (a 5³ of live hash probes was the rotation hitch).
	int nFrontHash = uSurfaceFrontierHashSize[surf];
	int nSnapHash = uSurfaceSnapHashSize[surf];
	if (nSnapHash > 0) {
		if (surfaceHashHas(surf, gi, nMemHash + nBlkHash + nFrontHash, nSnapHash, foundN)) {
			nrm = foundN;
			return true;
		}
		return false;
	}
	if (uSurfaceSnap == 0) return false;
	for (int di = -SURFACE_SNAP_CHEB; di <= SURFACE_SNAP_CHEB; di++) {
		for (int dj = -SURFACE_SNAP_CHEB; dj <= SURFACE_SNAP_CHEB; dj++) {
			for (int dk = -SURFACE_SNAP_CHEB; dk <= SURFACE_SNAP_CHEB; dk++) {
				if (di == 0 && dj == 0 && dk == 0) continue;
				ivec3 d = ivec3(di, dj, dk);
				if (!surfaceStepInPlane(d, planeN)) continue;
				if (surfaceHashHas(surf, gi + d, 0, nMemHash, foundN)) {
					nrm = foundN;
					memberIjk = gi + d;
					return true;
				}
			}
		}
	}
	return false;
}

vec3 applySurfaceHighlight(vec3 color, vec3 nodeLocalPos) {
	if (uSurfaceCount <= 0) return color;
	vec3 offsetP = (uNodeInPc * vec4(nodeLocalPos, 1.0)).xyz + uOffsetAdjust;
	vec3 outColor = color;
	for (int i = 0; i < SURFACE_MAX; i++) {
		if (i >= uSurfaceCount) break;
		float residual;
		vec3 nrm;
		ivec3 memberIjk;
		if (!surfaceMembership(i, offsetP, residual, nrm, memberIjk)) continue;
		if (uSurfaceGrowth[i] != 0) {
			int nMemHash = uSurfaceHashSize[i];
			int nBlkHash = uSurfaceShellHashSize[i];
			int nFrontHash = uSurfaceFrontierHashSize[i];
			int reason = 0;
			if (memberIjk.x >= 0 && surfaceHashReason(i, memberIjk, nMemHash + nBlkHash, nFrontHash, reason)) {
				if (reason == 1) outColor = vec3(0.13, 0.83, 0.93);
				else if (reason == 2) outColor = vec3(0.92, 0.70, 0.03);
				else if (reason == 3) outColor = vec3(0.91, 0.47, 0.98);
				else outColor = vec3(0.58, 0.64, 0.72);
			} else {
				outColor = uSurfaceColor[i];
			}
		} else if (uSurfaceRainbow[i] != 0) {
			// Flip swaps hollow/proud; membership still uses the pre-flip plane.
			// Zero is the grown centroid (+ user offset), not the picked point,
			// so re-picking on the same sheet does not shift the ramp.
			// Measured along the colouring normal (fitted plane or ideal plumb/level).
			float pr = dot(offsetP - uSurfacePlaneOrigin[i], uSurfaceProfileNormal[i]);
			float signedR = (uSurfaceFlipped[i] != 0 ? -pr : pr) - uSurfaceProfileLevel[i];
			// Each side of the zero level has its own scale: the hollow half of
			// the ramp spans -below .. 0, the proud half 0 .. +above.
			float below = uSurfaceProfileRangeBelow[i];
			float above = uSurfaceProfileRangeAbove[i];
			// Beyond the range the ramp says nothing about the point: keep its
			// own colour instead of saturating it into an end colour. The raw
			// range is the cull, so a zero side paints nothing on that side; the
			// floor only keeps the divisor finite (surfaceVolume.js mirrors this).
			if (signedR < -below || signedR > above) continue;
			float w = clamp(0.5 + 0.5 * signedR / max(signedR < 0.0 ? below : above, 1e-4), 0.0, 1.0);
			// Banded resolution: snap w to the centre of its band (half the bands
			// below zero, half above; the outer bands absorb the clamped ends).
			int bands = uSurfaceRainbow[i];
			if (bands > 1) {
				float n = float(bands);
				float k = min(floor(w * n), n - 1.0);
				w = (k + 0.5) / n;
			}
			outColor = texture(gradient, vec2(w, 1.0 - w)).rgb;
		} else {
			outColor = uSurfaceColor[i];
		}
	}
	return outColor;
}

float getPointSize(){
	float pointSize = 1.0;

	float slope = tan(fov / 2.0);
	float projFactor = -0.5 * uScreenHeight / (slope * vViewPosition.z);

	float scale = length(
		modelViewMatrix * vec4(0, 0, 0, 1) -
		modelViewMatrix * vec4(uOctreeSpacing, 0, 0, 1)
	) / uOctreeSpacing;
	projFactor = projFactor * scale;

	float r = uOctreeSpacing * 1.7;
	vRadius = r;
	#if defined fixed_point_size
		pointSize = size;
	#elif defined attenuated_point_size
		if(uUseOrthographicCamera){
			pointSize = size;
		}else{
			pointSize = size * spacing * projFactor;
			//pointSize = pointSize * projFactor;
		}
	#elif defined adaptive_point_size
		if(uUseOrthographicCamera) {
			float worldSpaceSize = 1.0 * size * r / getPointSizeAttenuation();
			pointSize = (worldSpaceSize / uOrthoWidth) * uScreenWidth;
		} else {
			float worldSpaceSize = 1.0 * size * r / getPointSizeAttenuation();
			pointSize = worldSpaceSize * projFactor;
		}
	#endif

	pointSize = max(minSize, pointSize);
	pointSize = min(maxSize, pointSize);

	vRadius = pointSize / projFactor;

	return pointSize;
}

#if defined(num_clippolygons) && num_clippolygons > 0
bool pointInClipPolygon(vec3 point, int polyIdx) {

	mat4 wvp = uClipPolygonWVP[polyIdx];
	//vec4 screenClipPos = uClipPolygonVP[polyIdx] * modelMatrix * vec4(point, 1.0);
	//screenClipPos.xy = screenClipPos.xy / screenClipPos.w * 0.5 + 0.5;

	vec4 pointNDC = wvp * vec4(point, 1.0);
	pointNDC.xy = pointNDC.xy / pointNDC.w;

	int j = uClipPolygonVCount[polyIdx] - 1;
	bool c = false;
	for(int i = 0; i < 8; i++) {
		if(i == uClipPolygonVCount[polyIdx]) {
			break;
		}

		//vec4 verti = wvp * vec4(uClipPolygonVertices[polyIdx * 8 + i], 1);
		//vec4 vertj = wvp * vec4(uClipPolygonVertices[polyIdx * 8 + j], 1);

		//verti.xy = verti.xy / verti.w;
		//vertj.xy = vertj.xy / vertj.w;

		//verti.xy = verti.xy / verti.w * 0.5 + 0.5;
		//vertj.xy = vertj.xy / vertj.w * 0.5 + 0.5;

		vec3 verti = uClipPolygonVertices[polyIdx * 8 + i];
		vec3 vertj = uClipPolygonVertices[polyIdx * 8 + j];

		if( ((verti.y > pointNDC.y) != (vertj.y > pointNDC.y)) &&
			(pointNDC.x < (vertj.x-verti.x) * (pointNDC.y-verti.y) / (vertj.y-verti.y) + verti.x) ) {
			c = !c;
		}
		j = i;
	}

	return c;
}
#endif

void doClipping(){

	{
		vec4 cl = getClassification();
		if(cl.a == 0.0){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);

			return;
		}
	}

	#if defined(clip_return_number_enabled)
	{ // return number filter
		vec2 range = uFilterReturnNumberRange;
		if(returnNumber < range.x || returnNumber > range.y){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);

			return;
		}
	}
	#endif

	#if defined(clip_number_of_returns_enabled)
	{ // number of return filter
		vec2 range = uFilterNumberOfReturnsRange;
		if(numberOfReturns < range.x || numberOfReturns > range.y){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);

			return;
		}
	}
	#endif

	#if defined(clip_gps_enabled)
	{ // GPS time filter
		float time = (gpsTime + uGpsOffset) * uGpsScale;
		vec2 range = uFilterGPSTimeClipRange;

		if(time < range.x || time > range.y){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);

			return;
		}
	}
	#endif

	#if defined(clip_point_source_id_enabled)
	{ // point source id filter
		vec2 range = uFilterPointSourceIDClipRange;
		if(pointSourceID < range.x || pointSourceID > range.y){
			gl_Position = vec4(100.0, 100.0, 100.0, 0.0);

			return;
		}
	}
	#endif

	int clipVolumesCount = 0;
	int insideCount = 0;
	vec3 clipHighlightColor = vec3(0.0);
	int clipHighlightCount = 0;

	#if defined(num_clipboxes) && num_clipboxes > 0
		for(int i = 0; i < num_clipboxes; i++){
			vec4 clipPosition = clipBoxes[i] * modelMatrix * vec4( position, 1.0 );
			bool inside = -0.5 <= clipPosition.x && clipPosition.x <= 0.5;
			inside = inside && -0.5 <= clipPosition.y && clipPosition.y <= 0.5;
			inside = inside && -0.5 <= clipPosition.z && clipPosition.z <= 0.5;

			insideCount = insideCount + (inside ? 1 : 0);
			clipVolumesCount++;
			if(inside){
				clipHighlightColor += clipBoxColors[i];
				clipHighlightCount++;
			}
		}
		if(clipHighlightCount > 0){
			clipHighlightColor /= float(clipHighlightCount);
		}
	#endif

	#if defined(num_clippolygons) && num_clippolygons > 0
		for(int i = 0; i < num_clippolygons; i++) {
			bool inside = pointInClipPolygon(position, i);

			insideCount = insideCount + (inside ? 1 : 0);
			clipVolumesCount++;
		}
	#endif

	bool insideAny = insideCount > 0;
	bool insideAll = (clipVolumesCount > 0) && (clipVolumesCount == insideCount);

	if(clipMethod == CLIPMETHOD_INSIDE_ANY){
		if(insideAny && clipTask == CLIPTASK_HIGHLIGHT){
			vColor.rgb = mix(vColor.rgb, clipHighlightColor, 0.5);
		}else if(!insideAny && clipTask == CLIPTASK_SHOW_INSIDE){
			gl_Position = vec4(100.0, 100.0, 100.0, 1.0);
		}else if(insideAny && clipTask == CLIPTASK_SHOW_OUTSIDE){
			gl_Position = vec4(100.0, 100.0, 100.0, 1.0);
		}
	}else if(clipMethod == CLIPMETHOD_INSIDE_ALL){
		if(insideAll && clipTask == CLIPTASK_HIGHLIGHT){
			vColor.rgb = mix(vColor.rgb, clipHighlightColor, 0.5);
		}else if(!insideAll && clipTask == CLIPTASK_SHOW_INSIDE){
			gl_Position = vec4(100.0, 100.0, 100.0, 1.0);
		}else if(insideAll && clipTask == CLIPTASK_SHOW_OUTSIDE){
			gl_Position = vec4(100.0, 100.0, 100.0, 1.0);
		}
	}
}



//
// ##     ##    ###    #### ##    ##
// ###   ###   ## ##    ##  ###   ##
// #### ####  ##   ##   ##  ####  ##
// ## ### ## ##     ##  ##  ## ## ##
// ##     ## #########  ##  ##  ####
// ##     ## ##     ##  ##  ##   ###
// ##     ## ##     ## #### ##    ##
//

void main() {
	vec4 mvPosition = modelViewMatrix * vec4(position, 1.0 );
	vViewPosition = mvPosition.xyz;
	gl_Position = projectionMatrix * mvPosition;
	vLogDepth = log2(-mvPosition.z);

	//gl_Position = vec4(0.0, 0.0, 0.0, 1.0);
	//gl_PointSize = 5.0;

	// POINT SIZE
	float pointSize = getPointSize();
	//float pointSize = 2.0;
	gl_PointSize = pointSize;
	vPointSize = pointSize;

	// COLOR
	vColor = getColor();
	// vColor = vec3(1.0, 0.0, 0.0);

	//gl_Position = vec4(0.0, 0.0, 0.0, 1.0);
	//gl_Position = vec4(position.xzy / 1000.0, 1.0 );

	//gl_PointSize = 5.0;
	//vColor = vec3(1.0, 1.0, 1.0);

	// only for "replacing" approaches
	// if(getLOD() != uLevel){
	// 	gl_Position = vec4(10.0, 10.0, 10.0, 1.0);
	// }


	#if defined hq_depth_pass
		float originalDepth = gl_Position.w;
		float adjustedDepth = originalDepth + 2.0 * vRadius;
		float adjust = adjustedDepth / originalDepth;

		mvPosition.xyz = mvPosition.xyz * adjust;
		gl_Position = projectionMatrix * mvPosition;
	#endif


	// CLIPPING
	doClipping();

	{
		vColor = applySurfaceHighlight(vColor, position);
	}

	#if defined(num_clipspheres) && num_clipspheres > 0
		for(int i = 0; i < num_clipspheres; i++){
			vec4 sphereLocal = uClipSpheres[i] * mvPosition;

			float distance = length(sphereLocal.xyz);

			if(distance < 1.0){
				float w = distance;
				vec3 cGradient = texture(gradient, vec2(w, 1.0 - w)).rgb;

				vColor = cGradient;
				//vColor = cGradient * 0.7 + vColor * 0.3;
			}
		}
	#endif

	#if defined(num_shadowmaps) && num_shadowmaps > 0

		const float sm_near = 0.1;
		const float sm_far = 10000.0;

		for(int i = 0; i < num_shadowmaps; i++){
			vec3 viewPos = (uShadowWorldView[i] * vec4(position, 1.0)).xyz;
			float distanceToLight = abs(viewPos.z);

			vec4 projPos = uShadowProj[i] * uShadowWorldView[i] * vec4(position, 1);
			vec3 nc = projPos.xyz / projPos.w;

			float u = nc.x * 0.5 + 0.5;
			float v = nc.y * 0.5 + 0.5;

			vec2 sampleStep = vec2(1.0 / (2.0*1024.0), 1.0 / (2.0*1024.0)) * 1.5;
			vec2 sampleLocations[9];
			sampleLocations[0] = vec2(0.0, 0.0);
			sampleLocations[1] = sampleStep;
			sampleLocations[2] = -sampleStep;
			sampleLocations[3] = vec2(sampleStep.x, -sampleStep.y);
			sampleLocations[4] = vec2(-sampleStep.x, sampleStep.y);

			sampleLocations[5] = vec2(0.0, sampleStep.y);
			sampleLocations[6] = vec2(0.0, -sampleStep.y);
			sampleLocations[7] = vec2(sampleStep.x, 0.0);
			sampleLocations[8] = vec2(-sampleStep.x, 0.0);

			float visibleSamples = 0.0;
			float numSamples = 0.0;

			float bias = vRadius * 2.0;

			for(int j = 0; j < 9; j++){
				vec4 depthMapValue = texture(uShadowMap[i], vec2(u, v) + sampleLocations[j]);

				float linearDepthFromSM = depthMapValue.x + bias;
				float linearDepthFromViewer = distanceToLight;

				if(linearDepthFromSM > linearDepthFromViewer){
					visibleSamples += 1.0;
				}

				numSamples += 1.0;
			}

			float visibility = visibleSamples / numSamples;

			if(u < 0.0 || u > 1.0 || v < 0.0 || v > 1.0 || nc.x < -1.0 || nc.x > 1.0 || nc.y < -1.0 || nc.y > 1.0 || nc.z < -1.0 || nc.z > 1.0){
				//vColor = vec3(0.0, 0.0, 0.2);
			}else{
				//vColor = vec3(1.0, 1.0, 1.0) * visibility + vec3(1.0, 1.0, 1.0) * vec3(0.5, 0.0, 0.0) * (1.0 - visibility);
				vColor = vColor * visibility + vColor * uShadowColor * (1.0 - visibility);
			}


		}

	#endif
}
`

Shaders["pointcloud.fs"] = `#version 300 es
// pointcloud.fs
precision highp float;

uniform mat4 viewMatrix;
uniform mat4 uViewInv;
uniform mat4 uProjInv;
uniform vec3 cameraPosition;


uniform mat4 projectionMatrix;
uniform float uOpacity;

uniform float blendHardness;
uniform float blendDepthSupplement;
uniform float fov;
uniform float uSpacing;
uniform float near;
uniform float far;
uniform float uPCIndex;
uniform float uScreenWidth;
uniform float uScreenHeight;

in vec3	vColor;
in float	vLogDepth;
in vec3	vViewPosition;
in float	vRadius;
in float 	vPointSize;
in vec3 	vPosition;

out vec4 out_FragColor;

float specularStrength = 1.0;

void main() {

	// gl_FragColor = vec4(vColor, 1.0);

	vec3 color = vColor;
	float depth = gl_FragCoord.z;

	#if defined(circle_point_shape) || defined(paraboloid_point_shape)
		float u = 2.0 * gl_PointCoord.x - 1.0;
		float v = 2.0 * gl_PointCoord.y - 1.0;
	#endif

	#if defined(circle_point_shape)
		float cc = u*u + v*v;
		if(cc > 1.0){
			discard;
		}
	#endif

	#if defined color_type_indices
		out_FragColor = vec4(color, uPCIndex / 255.0);
	#else
		out_FragColor = vec4(color, uOpacity);
	#endif

	#if defined paraboloid_point_shape
		float wi = 0.0 - ( u*u + v*v);
		vec4 pos = vec4(vViewPosition, 1.0);
		pos.z += wi * vRadius;
		float linearDepth = -pos.z;
		pos = projectionMatrix * pos;
		pos = pos / pos.w;
		float expDepth = pos.z;
		depth = (pos.z + 1.0) / 2.0;
		gl_FragDepth = depth;

		#if defined(color_type_depth)
			color.r = linearDepth;
			color.g = expDepth;
		#endif

		#if defined(use_edl)
			out_FragColor.a = log2(linearDepth);
		#endif

	#else
		#if defined(use_edl)
			out_FragColor.a = vLogDepth;
		#endif
	#endif

	#if defined(weighted_splats)
		float distance = 2.0 * length(gl_PointCoord.xy - 0.5);
		float weight = max(0.0, 1.0 - distance);
		weight = pow(weight, 1.5);

		out_FragColor.a = weight;
		out_FragColor.xyz = out_FragColor.xyz * weight;
	#endif
}


`

Shaders["pointcloud_sm.vs"] = `#version 300 es
precision mediump float;
// pointcloud_sm.vs
in vec3 position;
in vec3 color;

uniform mat4 modelMatrix;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat4 viewMatrix;

uniform float uScreenWidth;
uniform float uScreenHeight;
uniform float near;
uniform float far;

uniform float uSpacing;
uniform float uOctreeSize;
uniform float uLevel;
uniform float uVNStart;

uniform sampler2D visibleNodes;

out float vLinearDepth;
out vec3 vColor;

#define PI 3.141592653589793



// ---------------------
// OCTREE
// ---------------------

#if defined(adaptive_point_size)
/**
 * number of 1-bits up to inclusive index position
 * number is treated as if it were an integer in the range 0-255
 *
 */
float numberOfOnes(float number, float index){
	float tmp = mod(number, pow(2.0, index + 1.0));
	float numOnes = 0.0;
	for(float i = 0.0; i < 8.0; i++){
		if(mod(tmp, 2.0) != 0.0){
			numOnes++;
		}
		tmp = floor(tmp / 2.0);
	}
	return numOnes;
}


/**
 * checks whether the bit at index is 1
 * number is treated as if it were an integer in the range 0-255
 *
 */
bool isBitSet(float number, float index){
	return mod(floor(number / pow(2.0, index)), 2.0) != 0.0;
}


/**
 * find the LOD at the point position
 */
float getLOD(){

	vec3 offset = vec3(0.0, 0.0, 0.0);
	float iOffset = uVNStart;
	float depth = uLevel;
	for(float i = 0.0; i <= 30.0; i++){
		float nodeSizeAtLevel = uOctreeSize  / pow(2.0, i + uLevel + 0.0);

		vec3 index3d = (position-offset) / nodeSizeAtLevel;
		index3d = floor(index3d + 0.5);
		float index = 4.0 * index3d.x + 2.0 * index3d.y + index3d.z;

		vec4 value = texture2D(visibleNodes, vec2(iOffset / 2048.0, 0.0));
		float mask = value.r * 255.0;
		if(isBitSet(mask, index)){
			// there are more visible child nodes at this position
			iOffset = iOffset + value.g * 255.0 * 256.0 + value.b * 255.0 + numberOfOnes(mask, index - 1.0);
			depth++;
		}else{
			// no more visible child nodes at this position
			return depth;
		}

		offset = offset + (vec3(1.0, 1.0, 1.0) * nodeSizeAtLevel * 0.5) * index3d;
	}

	return depth;
}

#endif

float getPointSize(){
	float pointSize = 1.0;

	float slope = tan(fov / 2.0);
	float projFactor =  -0.5 * uScreenHeight / (slope * vViewPosition.z);

	float r = uOctreeSpacing * 1.5;
	vRadius = r;
	#if defined fixed_point_size
		pointSize = size;
	#elif defined attenuated_point_size
		if(uUseOrthographicCamera){
			pointSize = size;
		}else{
			pointSize = pointSize * projFactor;
		}
	#elif defined adaptive_point_size
		if(uUseOrthographicCamera) {
			float worldSpaceSize = 1.5 * size * r / getPointSizeAttenuation();
			pointSize = (worldSpaceSize / uOrthoWidth) * uScreenWidth;
		} else {
			float worldSpaceSize = 1.5 * size * r / getPointSizeAttenuation();
			pointSize = worldSpaceSize * projFactor;
		}
	#endif

	pointSize = max(minSize, pointSize);
	pointSize = min(maxSize, pointSize);

	vRadius = pointSize / projFactor;

	return pointSize;
}


void main() {

	vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
	vLinearDepth = gl_Position.w;

	float pointSize = getPointSize();
	gl_PointSize = pointSize;

}
`

Shaders["pointcloud_sm.fs"] = `#version 300 es
// pointcloud_sm.fs
precision mediump float;

in vec3 vColor;
out float vLinearDepth;

void main() {
	out_FragColor = vec4(vLinearDepth, vLinearDepth / 30.0, vLinearDepth / 30.0,  1.0);
}`

Shaders["normalize.vs"] = `#version 300 es
// normalize.vs
precision highp float;

in vec3 position;
in vec2 uv;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;

out vec2 vUv;

void main() {
	vUv = uv;
	gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
}`

Shaders["normalize.fs"] = `#version 300 es
// normalize.fs
precision highp float;

uniform sampler2D uWeightMap;
uniform sampler2D uDepthMap;

in  vec2 vUv;
out vec4 out_FragColor;

void main() {
	float depth = texture(uDepthMap, vUv).r;

	if(depth >= 1.0){
		discard;
	}

	out_FragColor = vec4(depth, 1.0, 0.0, 1.0);

	vec4 color = texture(uWeightMap, vUv);
	color = color / color.w;

	out_FragColor = vec4(color.xyz, 1.0);
	gl_FragDepth = depth;
}`

Shaders["normalize_and_edl.fs"] = `#version 300 es

//
// adapted from the EDL shader code from Christian Boucheny in cloud compare:
// https://github.com/cloudcompare/trunk/tree/master/plugins/qEDL/shaders/EDL
//

precision highp float;

uniform sampler2D uWeightMap;
uniform sampler2D uEDLMap;
uniform sampler2D uDepthMap;

uniform float screenWidth;
uniform float screenHeight;
uniform vec2 neighbours[NEIGHBOUR_COUNT];
uniform float edlStrength;
uniform float radius;

in  vec2 vUv;
out vec4 out_FragColor;

float response(float depth){
	vec2 uvRadius = radius / vec2(screenWidth, screenHeight);

	float sum = 0.0;

	for(int i = 0; i < NEIGHBOUR_COUNT; i++){
		vec2 uvNeighbor = vUv + uvRadius * neighbours[i];

		float neighbourDepth = texture(uEDLMap, uvNeighbor).a;

		if(neighbourDepth != 0.0){
			if(depth == 0.0){
				sum += 100.0;
			}else{
				sum += max(0.0, depth - neighbourDepth);
			}
		}
	}

	return sum / float(NEIGHBOUR_COUNT);
}

void main() {

	float edlDepth = texture(uEDLMap, vUv).a;
	float res = response(edlDepth);
	float shade = exp(-res * 300.0 * edlStrength);

	float depth = texture(uDepthMap, vUv).r;
	if(depth >= 1.0 && res == 0.0){
		discard;
	}

	vec4 color = texture(uWeightMap, vUv);
	color = color / color.w;
	color = color * shade;

	out_FragColor = vec4(color.xyz, 1.0);
	gl_FragDepth = depth;
}`

Shaders["edl.vs"] = `precision highp float;

in  vec3 position;
in  vec2 uv;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;

out vec2 vUv;

void main() {
	vUv = uv;

	vec4 mvPosition = modelViewMatrix * vec4(position,1.0);

	gl_Position = projectionMatrix * mvPosition;
}`

Shaders["edl.fs"] = `//
// adapted from the EDL shader code from Christian Boucheny in cloud compare:
// https://github.com/cloudcompare/trunk/tree/master/plugins/qEDL/shaders/EDL
//


precision highp float;

in vec2 vUv;
out vec4 out_FragColor;

uniform float screenWidth;
uniform float screenHeight;
uniform vec2 neighbours[NEIGHBOUR_COUNT];
uniform float edlStrength;
uniform float radius;
uniform float opacity;

uniform float uNear;
uniform float uFar;

uniform mat4 uProj;

uniform sampler2D uEDLColor;
uniform sampler2D uEDLDepth;

float response(float depth){
	vec2 uvRadius = radius / vec2(screenWidth, screenHeight);

	float sum = 0.0;

	for(int i = 0; i < NEIGHBOUR_COUNT; i++){
		vec2 uvNeighbor = vUv + uvRadius * neighbours[i];

		float neighbourDepth = texture(uEDLColor, uvNeighbor).a;
		neighbourDepth = (neighbourDepth == 1.0) ? 0.0 : neighbourDepth;

		if(neighbourDepth != 0.0){
			if(depth == 0.0){
				sum += 100.0;
			}else{
				sum += max(0.0, depth - neighbourDepth);
			}
		}
	}

	return sum / float(NEIGHBOUR_COUNT);
}

void main(){
	vec4 cEDL = texture(uEDLColor, vUv);

	float depth = cEDL.a;
	depth = (depth == 1.0) ? 0.0 : depth;
	float res = response(depth);
	float shade = exp(-res * 300.0 * edlStrength);

	out_FragColor = vec4(cEDL.rgb * shade, opacity);

	{ // write regular hyperbolic depth values to depth buffer
		float dl = pow(2.0, depth);

		vec4 dp = uProj * vec4(0.0, 0.0, -dl, 1.0);
		float pz = dp.z / dp.w;
		float fragDepth = (pz + 1.0) / 2.0;

		gl_FragDepth = fragDepth;
	}

	if(depth == 0.0){
		discard;
	}

}
`

Shaders["blur.vs"] = `#version 300 es
precision highp float;

in  vec3 position;
in  vec2 uv;
out vec2 vUv;

void main() {
	vUv = uv;

	gl_Position =   projectionMatrix * modelViewMatrix * vec4(position,1.0);
}`

Shaders["blur.fs"] = `#version 300 es
precision highp float;

uniform mat4 projectionMatrix;

uniform float screenWidth;
uniform float screenHeight;
uniform float near;
uniform float far;

uniform sampler2D map;

in  vec2 vUv;
out vec4 out_FragColor;

void main() {

	float dx = 1.0 / screenWidth;
	float dy = 1.0 / screenHeight;

	vec3 color = vec3(0.0, 0.0, 0.0);
	color += texture(map, vUv + vec2(-dx, -dy)).rgb;
	color += texture(map, vUv + vec2(  0, -dy)).rgb;
	color += texture(map, vUv + vec2(+dx, -dy)).rgb;
	color += texture(map, vUv + vec2(-dx,   0)).rgb;
	color += texture(map, vUv + vec2(  0,   0)).rgb;
	color += texture(map, vUv + vec2(+dx,   0)).rgb;
	color += texture(map, vUv + vec2(-dx,  dy)).rgb;
	color += texture(map, vUv + vec2(  0,  dy)).rgb;
	color += texture(map, vUv + vec2(+dx,  dy)).rgb;

	color = color / 9.0;

	out_FragColor = vec4(color, 1.0);
}`

export {Shaders};